import axios from 'axios';

// BUILD INFO for debugging - this will be logged on app load
const BUILD_INFO = {
  version: '2026.03.14.v3-PRODUCTION-FIX',
  timestamp: '2026-03-14T15:10:00Z',
  apiBase: '/api',
  note: 'FIXED-NO-HARDCODED-URLS'
};

// Log build info on load (helps debug deployment issues)
if (typeof window !== 'undefined') {
  console.log('%c=== APP BUILD INFO ===', 'background: #222; color: #bada55; font-size: 16px;');
  console.log('%cVersion: ' + BUILD_INFO.version, 'color: #00ff00; font-weight: bold;');
  console.log('%cAPI Base: ' + BUILD_INFO.apiBase, 'color: #00ff00;');
  console.log('%cCurrent Origin: ' + window.location.origin, 'color: #00ff00;');
  console.log('%cFull API URL: ' + window.location.origin + BUILD_INFO.apiBase, 'color: #00ff00;');
  console.log('%c======================', 'background: #222; color: #bada55; font-size: 16px;');
  
  // Also add to window for easy inspection
  window.APP_BUILD_INFO = BUILD_INFO;
  window.APP_API_URL = window.location.origin + BUILD_INFO.apiBase;
}

// CRITICAL: Always use relative URLs for production compatibility
// This ensures API calls go to /api/* on the SAME domain the app is served from
// No hardcoded URLs, no environment variables that could get stale
const BACKEND_URL = '';
const API_BASE = '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Property APIs
export const propertyAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Store APIs
export const storeAPI = {
  getAll: (params) => api.get('/stores', { params }),
  getById: (id) => api.get(`/stores/${id}`),
  create: (data) => api.post('/stores', data),
  update: (id, data) => api.put(`/stores/${id}`, data),
  delete: (id) => api.delete(`/stores/${id}`),
};

// User APIs
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Role APIs
export const roleAPI = {
  getAll: (params) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getPermissionModules: () => api.get('/permission-modules'),
};

// Unit APIs
export const unitAPI = {
  getAll: (params) => api.get('/units', { params }),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
};

// Category APIs
export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Vendor APIs
export const vendorAPI = {
  getAll: (params) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getPaymentAnalytics: (vendorId) => api.get('/payments/vendor-analytics', { params: { vendor_id: vendorId } }),
  downloadPaymentSummaryPDF: (vendorId) => api.get('/payments/summary-pdf', { 
    params: { vendor_id: vendorId },
    responseType: 'blob' 
  }),
};

// Item APIs
export const itemAPI = {
  getAll: (params) => api.get('/items', { params }),
  getById: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
};

// Stock Inward APIs
export const stockInwardAPI = {
  getAll: (params) => api.get('/stock-inward', { params }),
  create: (data) => api.post('/stock-inward', data),
  update: (id, data) => api.put(`/stock-inward/${id}`, data),
  delete: (id) => api.delete(`/stock-inward/${id}`),
};

// Stock Issue APIs
export const stockIssueAPI = {
  getAll: (params) => api.get('/stock-issue', { params }),
  create: (data) => api.post('/stock-issue', data),
  update: (id, data) => api.put(`/stock-issue/${id}`, data),
  downloadPDF: (id) => api.get(`/stock-issue/${id}/pdf`, { responseType: 'blob' }),
};

// Stock Transfer APIs
export const stockTransferAPI = {
  getAll: (params) => api.get('/stock-transfer', { params }),
  create: (data) => api.post('/stock-transfer', data),
};

// Stock APIs
export const stockAPI = {
  getAll: (params) => api.get('/stock', { params }),
  getSummary: (params) => api.get('/stock/summary', { params }),
  update: (id, data) => api.put(`/stock/${id}`, data),
  getAdjustments: (params) => api.get('/stock-adjustments', { params }),
  recalculate: () => api.post('/stock/recalculate'),
};

// Stock Acknowledgment APIs
export const stockAcknowledgmentAPI = {
  getPending: () => api.get('/stock-issue/pending-acknowledgment'),
  getDisputed: () => api.get('/stock-issue/disputed'),
  acknowledge: (id, data) => api.post(`/stock-issue/${id}/acknowledge`, data),
  acknowledgeItems: (id, data) => api.post(`/stock-issue/${id}/acknowledge-items`, data),
  dispute: (id, data) => api.post(`/stock-issue/${id}/dispute`, data),
  resolveDispute: (id, data) => api.post(`/stock-issue/${id}/resolve-dispute`, data),
};

// GRN Reconciliation API
export const grnReconciliationAPI = {
  getReconciliation: (params) => api.get('/grn/reconciliation', { params }),
  getStockVerification: (params) => api.get('/grn/stock-verification', { params }),
};

// Consumption APIs
export const consumptionAPI = {
  getAll: (params) => api.get('/consumption', { params }),
  getById: (id) => api.get(`/consumption/${id}`),
  create: (data) => api.post('/consumption', data),
  update: (id, data) => api.put(`/consumption/${id}`, data),
  delete: (id) => api.delete(`/consumption/${id}`),
  getStoreBalance: (storeId) => api.get(`/store/${storeId}/stock-balance`),
  uploadProof: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/consumption/upload-proof', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Dashboard API
export const dashboardAPI = {
  get: (params) => api.get('/dashboard', { params }),
};

// Reports API
export const reportsAPI = {
  getItemLedger: (params) => api.get('/reports/item-ledger', { params }),
  getLowStock: (params) => api.get('/reports/low-stock', { params }),
  getVendorPurchaseSummary: (params) => api.get('/reports/vendor-purchase-summary', { params }),
  getStoreStockSummary: (params) => api.get('/reports/store-stock-summary', { params }),
  getPropertyStockSummary: () => api.get('/reports/property-stock-summary'),
  getTransactionHistory: (params) => api.get('/reports/transaction-history', { params }),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Health API
export const healthAPI = {
  check: () => api.get('/health'),
};

// App Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Purchase Orders API
export const purchaseOrderAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  approve: (id) => api.post(`/purchase-orders/${id}/approve`),
  cancel: (id) => api.post(`/purchase-orders/${id}/cancel`),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  downloadPDF: (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  getReceivingStatus: (id) => api.get(`/purchase-orders/${id}/receiving-status`),
  getGRNs: (id) => api.get(`/purchase-orders/${id}/grn`),
  updatePayment: (id, data) => api.put(`/purchase-orders/${id}/payment`, data),
  recordPayment: (id, data) => api.post(`/purchase-orders/${id}/payments`, data),
  downloadBulkPaymentReportPDF: (poIds) => api.post('/purchase-orders/payment-report-pdf-bulk', { po_ids: poIds }, { responseType: 'blob' }),
  deletePayment: (id, paymentId) => api.delete(`/purchase-orders/${id}/payments/${paymentId}`),
  downloadPaymentReportPDF: (id) => api.get(`/purchase-orders/${id}/payment-report-pdf`, { responseType: 'blob' }),
  bulkAssignClient: (data) => api.post('/purchase-orders/bulk-assign-client', data),
  bulkApprove: (poIds) => api.post('/purchase-orders/bulk-approve', { po_ids: poIds }),
  getPaymentReminders: () => api.get('/purchase-orders/payment-reminders'),
  recalculateBalance: (id) => api.post(`/purchase-orders/${id}/recalculate-balance`),
  repairFromGRN: (id) => api.post(`/purchase-orders/${id}/repair-from-grn`),
  recalculateAll: () => api.post('/purchase-orders/recalculate-all'),
  downloadSummaryPDF: (poIds) => api.post('/purchase-orders/summary-pdf', poIds, { responseType: 'blob' }),
};

// Client/Event API
export const clientAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getPurchaseOrders: (id) => api.get(`/clients/${id}/purchase-orders`),
  getEventTypes: () => api.get('/event-types'),
  downloadExpenseSummary: (clientIds) => api.post('/clients/expense-summary-pdf', clientIds, { responseType: 'blob' }),
};

// Petty Cash API
export const pettyCashAPI = {
  // Transfers
  createTransfer: (data) => api.post('/petty-cash/transfer', data),
  updateTransfer: (id, data) => api.put(`/petty-cash/transfer/${id}`, data),
  // Expenses
  createExpense: (data) => api.post('/petty-cash/expense', data),
  updateExpense: (id, data) => api.put(`/petty-cash/expense/${id}`, data),
  // Returns
  createReturn: (data) => api.post('/petty-cash/return', data),
  // Balance
  getMyBalance: () => api.get('/petty-cash/my-balance'),
  getUserBalance: (userId) => api.get(`/petty-cash/balance/${userId}`),
  // Transactions (with sort support)
  getTransactions: (params) => api.get('/petty-cash/transactions', { params }),
  getPendingApprovals: (status) => api.get('/petty-cash/pending-approvals', { params: status ? { status } : {} }),
  // Dashboard
  getDashboard: () => api.get('/petty-cash/dashboard'),
  // Categories
  getExpenseCategories: () => api.get('/petty-cash/expense-categories'),
  // Analytics for reports
  getAnalytics: (params) => api.get('/petty-cash/analytics', { params }),
  // Bill upload (for new expenses)
  uploadBill: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/petty-cash/upload-bill', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Upload bill to existing expense
  uploadExpenseBill: (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/petty-cash/expense/${expenseId}/upload-bill`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Expenses without bills (for notifications)
  getExpensesWithoutBills: () => api.get('/petty-cash/expenses-without-bills'),
  // PDF Expense Report
  downloadExpenseReport: (params) => api.get('/petty-cash/expense-report/pdf', { 
    params, 
    responseType: 'blob' 
  }),
  // Soft delete entry (Super Admin only)
  deleteEntry: (id) => api.delete(`/petty-cash/entry/${id}`),
  // Restore deleted entry (Super Admin only)
  restoreEntry: (id) => api.post(`/petty-cash/entry/${id}/restore`),
  // Get deleted entries (Super Admin only)
  getDeletedEntries: () => api.get('/petty-cash/deleted'),
  // Get transfers for a specific user (Admin only)
  getUserTransfers: (userId) => api.get(`/petty-cash/user/${userId}/transfers`),
  // Bulk upload transfers from Excel
  bulkUploadTransfers: (formData) => api.post('/petty-cash/transfer/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  // Undo bulk upload
  undoBatch: (batchId) => api.delete(`/petty-cash/transfer/batch/${batchId}`),
  // Bulk delete transfers by IDs
  bulkDeleteTransfers: (ids) => api.post('/petty-cash/transfer/bulk-delete', { ids }),
};

// GRN (Goods Receipt Note) API
export const grnAPI = {
  getAll: (params) => api.get('/grn', { params }),
  getById: (id) => api.get(`/grn/${id}`),
  create: (data) => api.post('/grn', data),
  update: (id, data) => api.put(`/grn/${id}`, data),
  delete: (id) => api.delete(`/grn/${id}`),
  merge: (grn_ids) => api.post('/grn/merge', { grn_ids }),
  downloadPDF: (id) => api.get(`/grn/${id}/pdf`, { responseType: 'blob' }),
  getExceptionsDashboard: (params) => api.get('/grn/exceptions/dashboard', { params }),
  getGSTReport: (params) => api.get('/grn/gst-report', { params }),
  uploadBill: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/grn/${id}/upload-bill`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Service Received Note API (for Service POs)
export const srnAPI = {
  getAll: (params) => api.get('/srn', { params }),
  getById: (id) => api.get(`/srn/${id}`),
  create: (data) => api.post('/srn', data),
  update: (id, data) => api.put(`/srn/${id}`, data),
  delete: (id) => api.delete(`/srn/${id}`),
  merge: (srnIds) => api.post('/srn/merge', srnIds),
  recalculate: (id) => api.post(`/srn/${id}/recalculate`),
};

// Service Items Master API
export const serviceItemAPI = {
  getAll: (params) => api.get('/service-items', { params }),
  getById: (id) => api.get(`/service-items/${id}`),
  create: (data) => api.post('/service-items', data),
  update: (id, data) => api.put(`/service-items/${id}`, data),
  delete: (id) => api.delete(`/service-items/${id}`),
  getCategories: () => api.get('/service-categories'),
};

// Service PO specific functions
export const servicePOAPI = {
  recalculateBalance: (id) => api.post(`/service-orders/${id}/recalculate-balance`),
  repairFromSRN: (id) => api.post(`/service-orders/${id}/repair-from-srn`),
};

// Service PO Templates API
export const serviceTemplateAPI = {
  getAll: (params) => api.get('/service-templates', { params }),
  getById: (id) => api.get(`/service-templates/${id}`),
  create: (data) => api.post('/service-templates', data),
  update: (id, data) => api.put(`/service-templates/${id}`, data),
  delete: (id) => api.delete(`/service-templates/${id}`),
  createPO: (id, data) => api.post(`/service-templates/${id}/create-po`, null, { params: data }),
};

// Audit Logs API
export const auditLogAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getSummary: () => api.get('/audit-logs/summary'),
  getActions: () => api.get('/audit-logs/actions'),
  getEntityTypes: () => api.get('/audit-logs/entity-types'),
};

// Indents/Requisitions API
export const indentAPI = {
  getAll: (params) => api.get('/indents', { params }),
  getById: (id) => api.get(`/indents/${id}`),
  create: (data) => api.post('/indents', data),
  approve: (id, data) => api.put(`/indents/${id}/approve`, data),
  reject: (id, data) => api.put(`/indents/${id}/reject`, data),
  cancel: (id) => api.delete(`/indents/${id}`),
  getPending: (params) => api.get('/indents/pending', { params }),
  getDashboard: () => api.get('/indents/dashboard'),
};

// User Preferences API
export const preferencesAPI = {
  get: (page) => api.get(`/user-preferences/${page}`),
  update: (page, data) => api.put(`/user-preferences/${page}`, { page, ...data }),
};

// Data Export API
export const exportAPI = {
  export: (data) => api.post('/export', data, { responseType: 'blob' }),
};

// Spell Check API
export const spellCheckAPI = {
  check: (text, context) => api.post('/spell-check', { text, context }),
};

// ============== BILL/FILE URL UTILITIES ==============
// These utilities ensure bill URLs work correctly regardless of where they were originally saved

/**
 * Get the correct URL for viewing a bill/file
 * Handles old URLs that may have wrong domain names
 * Works correctly in both preview and production environments
 * @param {string} url - The URL stored in the database
 * @returns {string|null} - The corrected URL for viewing
 */
export const getBillUrl = (url) => {
  if (!url) return null;
  
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  
  let billPath = url;
  
  // If it's an absolute URL, extract just the path portion
  if (billPath.startsWith('http://') || billPath.startsWith('https://')) {
    try {
      const urlObj = new URL(billPath);
      billPath = urlObj.pathname;
    } catch (e) {
      const match = billPath.match(/\/uploads\/.+$/);
      if (match) {
        billPath = match[0];
      }
    }
  }
  
  // Ensure path starts with /
  if (!billPath.startsWith('/')) {
    billPath = '/' + billPath;
  }
  
  // Route /uploads/filename through /api/files/filename for K8s ingress compatibility
  if (billPath.startsWith('/uploads/')) {
    billPath = '/api/files/' + billPath.substring('/uploads/'.length);
  }
  
  return `${currentOrigin}${billPath}`;
};

/**
 * Download a bill/file
 * @param {string} url - The URL of the file to download
 * @param {string} filename - Optional filename for the download
 */
export const downloadBill = async (url, filename = null) => {
  if (!url) return;
  
  const correctUrl = getBillUrl(url);
  if (!correctUrl) return;
  
  try {
    const response = await fetch(correctUrl);
    const blob = await response.blob();
    
    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Generate filename if not provided
    if (!filename) {
      const urlParts = correctUrl.split('/');
      filename = urlParts[urlParts.length - 1] || 'bill_download';
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Failed to download file:', error);
    // Fallback: open in new tab
    window.open(correctUrl, '_blank');
  }
};

/**
 * View a bill/file in a new tab
 * @param {string} url - The URL of the file to view
 */
export const viewBill = (url) => {
  if (!url) return;
  const correctUrl = getBillUrl(url);
  if (correctUrl) {
    window.open(correctUrl, '_blank');
  }
};

// Bank Analytics API (Super Admin Only)
export const bankAnalyticsAPI = {
  // Upload
  uploadStatement: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bank-analytics/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importStatement: (uploadId, columnMapping, skipDuplicates = true) => 
    api.post(`/bank-analytics/import/${uploadId}`, null, {
      params: { skip_duplicates: skipDuplicates },
      data: columnMapping
    }),
  getUploads: () => api.get('/bank-analytics/uploads'),
  deleteUpload: (uploadId) => api.delete(`/bank-analytics/uploads/${uploadId}`),
  
  // Transactions
  getTransactions: (params) => api.get('/bank-analytics/transactions', { params }),
  getTransaction: (id) => api.get(`/bank-analytics/transactions/${id}`),
  updateTransaction: (id, data) => api.put(`/bank-analytics/transactions/${id}`, data),
  bulkUpdateTransactions: (transactionIds, updates) => 
    api.post('/bank-analytics/transactions/bulk-update', { transaction_ids: transactionIds, updates }),
  markReviewed: (id) => api.post(`/bank-analytics/transactions/${id}/mark-reviewed`),
  
  // Dashboard
  getDashboard: (params) => api.get('/bank-analytics/dashboard', { params }),
  getFilters: () => api.get('/bank-analytics/filters'),
  
  // Export
  exportTransactions: (params) => api.get('/bank-analytics/export', { params, responseType: 'blob' }),
};

// Investors API
export const investorsAPI = {
  getAll: (params) => api.get('/investors', { params }),
  create: (data) => api.post('/investors', data),
  update: (id, data) => api.put(`/investors/${id}`, data),
  delete: (id) => api.delete(`/investors/${id}`),
};

// Expense Types API
export const expenseTypesAPI = {
  getAll: (params) => api.get('/expense-types', { params }),
  create: (data) => api.post('/expense-types', data),
  update: (id, data) => api.put(`/expense-types/${id}`, data),
  delete: (id) => api.delete(`/expense-types/${id}`),
};

// Menu PO Assistant API
export const menuPOAPI = {
  analyze: (formData) => api.post('/menu-po/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  listAnalyses: () => api.get('/menu-po/analyses'),
  getAnalysis: (id) => api.get(`/menu-po/analyses/${id}`),
  deleteAnalysis: (id) => api.delete(`/menu-po/analyses/${id}`),
  exportExcel: (data) => api.post('/menu-po/export-excel', data, { responseType: 'blob' }),
};

export default api;
